import React, { useMemo, useState, useCallback, useEffect } from 'react'

import { Box, Progress, Text, Tooltip } from '@chakra-ui/react'
import {
  SurfaceCard,
  Stack,
  HStack,
  Typography,
  Button,
  DataGrid,
  Pagination,
  TColumnDef,
  Tag,
  useToast,
  EmptyState,
} from '@pnld/components-web'

import {
  useProcessingSchools,
  useProcessingSchoolsMetrics,
  useProcessingSchoolsFilters,
  useExportTechnicalAssignment,
} from '@/api/processing/queries'
import {
  requestProcessingSchoolsReport,
  requestCalculationMemoryReport,
  getProcessingSchoolsReportStatus,
} from '@/api/processing/services'
import {
  ServiceMapFilterParams,
  ShowOptedOutFilter,
} from '@/api/processing/types'
import DataFilter, { IFilter, TFilter } from '@/components/DataFilter'
import TotalizerDisplay from '@/components/Display/Totalizer'
import useReportDownload from '@/hooks/useReportDownload'
import { ProcessingSchool } from '@/types/processing'
import { getErrorMessage } from '@/utils/api'

import {
  serviceMapFilters,
  serviceMapFilterState,
  withServiceMapColumns,
} from '../../serviceMap'
import { ProcessingCardProps } from './types'

const ProcessingCard: React.FC<ProcessingCardProps> = ({
  processingId,
  hasServiceMap = false,
}) => {
  const toast = useToast()

  const [pagination, setPagination] = useState<{
    pageIndex: number
    pageSize: number
  }>({
    pageIndex: 0,
    pageSize: 10,
  })

  // rascunho (muda ao digitar/selecionar)
  const [draftFilters, setDraftFilters] = useState<
    {
      search?: string
      initialDate?: string
      finalDate?: string
      dsSphere?: string
      dsState?: string
      dsCity?: string
      coComponent?: number
      dsCollectionCode?: string
      dsPublisher?: string
      showOptedOut?: ShowOptedOutFilter
    } & ServiceMapFilterParams
  >({})

  const [appliedFilters, setAppliedFilters] = useState<typeof draftFilters>({})
  const [hasSearched, setHasSearched] = useState(true)

  const parseDate = useCallback((value?: string | null) => {
    if (!value) return undefined
    const [day, month, year] = value.split('/')
    if (!day || !month || !year) return undefined
    return `${year}-${month}-${day}`
  }, [])

  const mapFilterToState = useCallback(
    (filter: TFilter) => ({
      search: filter.search || undefined,
      initialDate: parseDate(filter.startDate as string | null),
      finalDate: parseDate(filter.finalDate as string | null),
      dsSphere: filter.dsSphere || undefined,
      dsState: filter.dsState || undefined,
      dsCity: filter.dsCity || undefined,
      coComponent: filter.coComponent ? Number(filter.coComponent) : undefined,
      dsCollectionCode: filter.dsCollectionCode || undefined,
      dsPublisher: filter.dsPublisher || undefined,
      showOptedOut:
        (filter.showOptedOut as ShowOptedOutFilter | undefined) || undefined,
      ...serviceMapFilterState(filter),
    }),
    [parseDate]
  )

  const handleOnChangeFilter = useCallback(
    (filter: TFilter) => {
      setDraftFilters(mapFilterToState(filter))
    },
    [mapFilterToState]
  )

  const handleOnSearch = useCallback(
    (clearedValues?: typeof draftFilters) => {
      setAppliedFilters(clearedValues ?? draftFilters)
      setHasSearched(true)
    },
    [draftFilters]
  )

  const listPayload = useMemo(
    () => ({
      processingId: processingId as number,
      page: pagination.pageIndex + 1,
      qnt: pagination.pageSize,
      ...appliedFilters,
    }),
    [processingId, pagination, appliedFilters]
  )

  const { data: listData } = useProcessingSchools(listPayload, {
    keepPreviousData: true,
    enabled: Boolean(processingId) && hasSearched,
  })

  const { data: serverFiltersData, isLoading: isLoadingFilters } =
    useProcessingSchoolsFilters(
      { processingId: processingId as number, dsState: draftFilters.dsState },
      { enabled: Boolean(processingId) }
    )

  const { data: metricsData } = useProcessingSchoolsMetrics(
    { processingId: processingId as number, ...appliedFilters },
    { enabled: Boolean(processingId) && hasSearched }
  )

  const requestReport = useCallback(
    () =>
      requestProcessingSchoolsReport({
        processingId: processingId as number,
        ...appliedFilters,
      }),
    [processingId, appliedFilters]
  )

  const getReportStatus = useCallback(
    (jobId: string) =>
      getProcessingSchoolsReportStatus({
        processingId: processingId as number,
        jobId,
      }),
    [processingId]
  )

  const {
    state: exportState,
    start: handleExport,
    isLoading: isExporting,
  } = useReportDownload({
    request: requestReport,
    getStatus: getReportStatus,
  })

  const {
    refetch: refetchTechnicalAssignment,
    isFetching: isExportingTechnicalAssignment,
  } = useExportTechnicalAssignment(
    { processingId: processingId as number },
    { enabled: false }
  )

  // Memória de cálculo: mesmo fluxo assíncrono do export de escolas
  // (request-report → polling de status → download automático). O status
  // é servido pelo mesmo endpoint dos relatórios de escolas.
  const requestCalculationMemory = useCallback(
    () =>
      requestCalculationMemoryReport({ processingId: processingId as number }),
    [processingId]
  )

  const {
    state: calculationMemoryState,
    start: handleExportCalculationMemory,
    isLoading: isExportingCalculationMemory,
  } = useReportDownload({
    request: requestCalculationMemory,
    getStatus: getReportStatus,
  })

  // PSV-460: a mesma memória em CSV compactado, gerada pelo banco — segundos,
  // contra dezenas de minutos do xlsx no volume do PNLD 2027. Job e progresso
  // próprios: os dois formatos podem ser pedidos ao mesmo tempo.
  const requestCalculationMemoryCsv = useCallback(
    () =>
      requestCalculationMemoryReport({
        processingId: processingId as number,
        formato: 'csv',
      }),
    [processingId]
  )

  const {
    state: calculationMemoryCsvState,
    start: handleExportCalculationMemoryCsv,
    isLoading: isExportingCalculationMemoryCsv,
  } = useReportDownload({
    request: requestCalculationMemoryCsv,
    getStatus: getReportStatus,
  })

  useEffect(() => {
    setPagination(p => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }))
  }, [appliedFilters])

  const handleExportTechnicalAssignment = useCallback(async () => {
    try {
      const { data } = await refetchTechnicalAssignment()
      if (!data) {
        toast({
          status: 'error',
          title: 'Ops! Ocorreu uma falha durante a exportação.',
        })
        return
      }
      const blobUrl = window.URL.createObjectURL(data)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `processamento_${processingId}_atribuicao_tecnica.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast({ status: 'success', title: 'Exportação concluída com sucesso!' })
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000)
    } catch (error) {
      toast({ status: 'error', title: getErrorMessage(error) })
    }
  }, [refetchTechnicalAssignment, processingId, toast])

  const numberFormatter = (value: number) =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 0 })

  const totals = useMemo(
    () => ({
      nuQuantityCensus: metricsData?.nuQuantityCensus || 0,
      nuQuantityQt1: metricsData?.nuQuantityQt1 || 0,
      nuQuantityQt2: metricsData?.nuQuantityQt2 || 0,
    }),
    [metricsData]
  )

  const baseColumns: Array<TColumnDef<ProcessingSchool>> = useMemo(
    () => [
      {
        id: 'school',
        header: 'Escola',
        enableSorting: false,
        cell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="s-xs">{row.original.noSchool}</Typography>
            <Typography variant="s-xs" color="neutral.dark_2">
              INEP: {row.original.dsSchoolInepCode}
            </Typography>
          </Stack>
        ),
      },
      { accessorKey: 'dsSphere', header: 'Esfera', enableSorting: false },
      {
        id: 'location',
        header: 'Município/Estado',
        enableSorting: false,
        cell: ({ row }) => `${row.original.dsCity} / ${row.original.dsState}`,
      },
      { accessorKey: 'dsYearGrade', header: 'Ano/Série', enableSorting: false },
      {
        accessorKey: 'dsComponent',
        header: 'Componente',
        enableSorting: false,
      },
      {
        id: 'collection',
        header: 'Coleção',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.inOptedOut ? (
            <Tag variant="neutral" sx={{ textWrap: 'nowrap' }}>
              Optou por não receber
            </Tag>
          ) : (
            <Stack spacing={0.25}>
              <Typography variant="s-xs">
                {row.original.noCollection}
              </Typography>
              <Typography variant="s-xs" color="neutral.dark_2">
                {row.original.dsCollectionCode}
              </Typography>
            </Stack>
          ),
      },
      { accessorKey: 'dsPublisher', header: 'Editora', enableSorting: false },
      {
        accessorKey: 'nuQuantityCensus',
        header: 'Quant. Censo',
        enableSorting: false,
        cell: ({ row }) => numberFormatter(row.original.nuQuantityCensus),
      },
      {
        accessorKey: 'nuQuantityQt1',
        header: 'Quant. QT1',
        enableSorting: false,
        cell: ({ row }) => numberFormatter(row.original.nuQuantityQt1),
      },
      {
        accessorKey: 'nuQuantityQt2',
        header: 'Quant. QT2',
        enableSorting: false,
        cell: ({ row }) => numberFormatter(row.original.nuQuantityQt2),
      },
    ],
    []
  )

  const columns = useMemo(
    () => withServiceMapColumns(baseColumns, hasServiceMap),
    [baseColumns, hasServiceMap]
  )

  const numberOfItems = listData?.count || 0
  const numberOfPages = Math.max(
    1,
    Math.ceil(numberOfItems / (pagination.pageSize || 1))
  )

  const choicesOfItemsPerPage = useMemo(
    () => [10, 20, 50].map(value => ({ label: String(value), value })),
    []
  )

  const choicesOfPageJump = useMemo(() => {
    const limit = Math.min(numberOfPages, 50)
    return Array.from({ length: limit }, (_, i) => ({
      label: String(i + 1),
      value: i,
    }))
  }, [numberOfPages])

  const handlePrevPage = () => {
    setPagination(p => ({ ...p, pageIndex: Math.max(p.pageIndex - 1, 0) }))
  }
  const handleNextPage = () => {
    setPagination(p => ({
      ...p,
      pageIndex: Math.min(p.pageIndex + 1, Math.max(0, numberOfPages - 1)),
    }))
  }
  const handleJumpToPage = (value: number) => {
    setPagination(p => ({ ...p, pageIndex: value }))
  }
  const handleChangeItemsPerPage = (value: number) => {
    setPagination(p => ({ pageIndex: 0, pageSize: value }))
  }

  return (
    <SurfaceCard padding={4}>
      <Stack spacing={4}>
        <DataFilter
          filters={useMemo(() => {
            const dsSpheres = serverFiltersData?.dsSpheres || []
            const dsStates = serverFiltersData?.dsStates || []
            const dsCities = serverFiltersData?.dsCities || []
            const coComponents = serverFiltersData?.coComponents || []
            const dsCollectionCodes = serverFiltersData?.dsCollectionCodes || []
            const dsPublishers = serverFiltersData?.dsPublishers || []

            const mapped: Array<IFilter> = [
              {
                type: 'select',
                name: 'dsSphere',
                placeholder: 'Esfera',
                options: dsSpheres.map(sphere => ({
                  label: sphere,
                  value: sphere,
                })),
              },
              {
                type: 'select',
                name: 'dsState',
                placeholder: 'Estado',
                options: dsStates.map(state => ({
                  label: state.label,
                  value: state.value,
                })),
              },
              {
                type: 'select',
                name: 'dsCity',
                placeholder: 'Município',
                options: dsCities.map(city => ({ label: city, value: city })),
                isDisabled: !draftFilters.dsState,
                isLoading: isLoadingFilters,
              },
              {
                type: 'select',
                name: 'coComponent',
                placeholder: 'Componente',
                options: coComponents.map(component => ({
                  label: component.label,
                  value: String(component.value),
                })),
              },
              {
                type: 'select',
                name: 'dsCollectionCode',
                placeholder: 'Cod. da coleção',
                options: dsCollectionCodes.map(collectionCode => ({
                  label: collectionCode,
                  value: collectionCode,
                })),
              },
              {
                type: 'select',
                name: 'dsPublisher',
                placeholder: 'Editora',
                options: dsPublishers.map(publisher => ({
                  label: publisher,
                  value: publisher,
                })),
              },
              {
                type: 'select',
                name: 'showOptedOut',
                placeholder: 'Optou por não receber',
                options: [
                  { label: 'Ocultar', value: 'hide' },
                  { label: 'Mostrar somente', value: 'only' },
                ],
              },
            ]

            return serviceMapFilters(mapped, serverFiltersData, hasServiceMap)
          }, [serverFiltersData, draftFilters.dsState, hasServiceMap])}
          filtersPerRow={3}
          hasDate={false}
          onChangeFilter={handleOnChangeFilter}
          onSearch={handleOnSearch}
        />

        <HStack spacing={4}>
          <TotalizerDisplay
            number={totals.nuQuantityCensus}
            label="Total de livros | Base: Censo"
            icon="book"
            fullWidth
            small
          />
          <TotalizerDisplay
            variant="highlight"
            number={totals.nuQuantityQt1}
            label="Total de livros | Base: QT1"
            icon="book"
            fullWidth
            small
          />
          <TotalizerDisplay
            variant="highlight"
            number={totals.nuQuantityQt2}
            label="Total de livros | Base: QT2"
            icon="book"
            fullWidth
            small
          />
        </HStack>

        <HStack
          spacing={4}
          justifyContent="space-between"
          flexWrap="wrap"
          rowGap={2}
        >
          <Typography variant="h-xs" color="brand.primary.dark_2">
            Processamento por Escola
          </Typography>
          <HStack spacing={2} flexWrap="wrap" rowGap={2}>
            <Tooltip
              label={
                'Gera um XLSX com duas abas: "Memória de Cálculo", com todas as ' +
                'escolas e os insumos usados na apuração, e "Ranking da Apuração", ' +
                'com votos e posição de cada coleção disputante. O ranking só ' +
                'existe nos modelos de escolha por rede ou por grupo — no modelo ' +
                'único por escola não há disputa, e a aba vem vazia.'
              }
              placement="top"
              hasArrow
              maxW="360px"
            >
              <Box display="inline-block">
                <Button
                  variant="secondary"
                  label="Exportar Memória de Cálculo"
                  iconName="download"
                  border="square"
                  onClick={handleExportCalculationMemory}
                  isLoading={isExportingCalculationMemory}
                  loadingText={
                    calculationMemoryState.progressPct > 0
                      ? `${calculationMemoryState.progressPct}% concluído`
                      : 'Carregando...'
                  }
                  disabled={isExportingCalculationMemory}
                />
              </Box>
            </Tooltip>
            <Tooltip
              label={
                'Mesmo conteúdo da Memória de Cálculo, em CSV (separado por ' +
                'ponto e vírgula) dentro de um arquivo .zip: a memória em partes ' +
                'de até 1 milhão de linhas, que é o limite do Excel, e o ranking ' +
                'da apuração. Fica pronto bem mais rápido que o XLSX.'
              }
              placement="top"
              hasArrow
              maxW="360px"
            >
              <Box display="inline-block">
                <Button
                  variant="secondary"
                  label="Exportar Memória (CSV .zip)"
                  iconName="download"
                  border="square"
                  onClick={handleExportCalculationMemoryCsv}
                  isLoading={isExportingCalculationMemoryCsv}
                  loadingText={
                    calculationMemoryCsvState.progressPct > 0
                      ? `${calculationMemoryCsvState.progressPct}% concluído`
                      : 'Carregando...'
                  }
                  disabled={isExportingCalculationMemoryCsv}
                />
              </Box>
            </Tooltip>
            <Button
              variant="secondary"
              label="Exportar Atribuição Técnica"
              iconName="download"
              border="square"
              onClick={handleExportTechnicalAssignment}
              isLoading={isExportingTechnicalAssignment}
              disabled={isExportingTechnicalAssignment}
            />
            <Button
              variant="primary"
              label="Exportar"
              iconName="download"
              border="square"
              onClick={handleExport}
              isLoading={isExporting}
              loadingText={
                exportState.progressPct > 0
                  ? `${exportState.progressPct}% concluído`
                  : 'Carregando...'
              }
              disabled={isExporting}
            />
          </HStack>
        </HStack>

        {isExporting && (
          <Stack gap={1}>
            <Text fontSize="xs" color="brand.neutral.dark_2">
              Gerando exportação de escolas...{' '}
              {exportState.progressPct > 0
                ? `${
                    exportState.progressPct
                  }% (${exportState.progressRows.toLocaleString(
                    'pt-BR'
                  )} de ${exportState.totalRows.toLocaleString(
                    'pt-BR'
                  )} linhas)`
                : 'Aguarde. Isso pode levar alguns minutos.'}
            </Text>
            <Progress
              value={exportState.progressPct}
              size="sm"
              colorScheme="blue"
              borderRadius="md"
              isAnimated
              hasStripe
            />
          </Stack>
        )}

        {isExportingCalculationMemory && (
          <Stack gap={1}>
            <Text fontSize="xs" color="brand.neutral.dark_2">
              Gerando memória de cálculo...{' '}
              {calculationMemoryState.progressPct > 0
                ? `${
                    calculationMemoryState.progressPct
                  }% (${calculationMemoryState.progressRows.toLocaleString(
                    'pt-BR'
                  )} de ${calculationMemoryState.totalRows.toLocaleString(
                    'pt-BR'
                  )} linhas)`
                : 'Aguarde. Isso pode levar alguns minutos.'}
            </Text>
            <Progress
              value={calculationMemoryState.progressPct}
              size="sm"
              colorScheme="blue"
              borderRadius="md"
              isAnimated
              hasStripe
            />
          </Stack>
        )}

        {isExportingCalculationMemoryCsv && (
          <Stack gap={1}>
            <Text fontSize="xs" color="brand.neutral.dark_2">
              Gerando memória de cálculo em CSV...{' '}
              {calculationMemoryCsvState.progressPct > 0
                ? `${
                    calculationMemoryCsvState.progressPct
                  }% (${calculationMemoryCsvState.progressRows.toLocaleString(
                    'pt-BR'
                  )} de ${calculationMemoryCsvState.totalRows.toLocaleString(
                    'pt-BR'
                  )} linhas)`
                : 'Aguarde.'}
            </Text>
            <Progress
              value={calculationMemoryCsvState.progressPct}
              size="sm"
              colorScheme="blue"
              borderRadius="md"
              isAnimated
              hasStripe
            />
          </Stack>
        )}

        <Stack spacing={0}>
          <Stack overflowX="auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            {listData?.results.length ? (
              // PSV-426: com mapa entram Categoria, Área e Etapas Atendidas.
              <Stack minW={hasServiceMap ? '2000px' : '1600px'}>
                <DataGrid
                  columns={columns}
                  data={listData?.results || []}
                  hasPagination={false}
                />
              </Stack>
            ) : (
              <EmptyState
                title="Nenhuma escola encontrada"
                description="Ajuste os filtros ou tente novamente mais tarde."
                shaped="false"
                iconProps={{ name: 'table' }}
              />
            )}
          </Stack>

          {listData?.results.length ? (
            <Pagination
              currentPageIndex={pagination.pageIndex}
              itemsPerPage={pagination.pageSize}
              numberOfItems={numberOfItems}
              numberOfPages={numberOfPages}
              choicesOfItemsPerPage={choicesOfItemsPerPage}
              choicesOfPageJump={choicesOfPageJump}
              onSelectPreviousPage={handlePrevPage}
              onSelectNextPage={handleNextPage}
              onJumpToPage={handleJumpToPage}
              onChangeItemsPerPage={handleChangeItemsPerPage}
            />
          ) : null}
        </Stack>
      </Stack>
    </SurfaceCard>
  )
}

export default ProcessingCard
